import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import { TranslatableInputText } from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import {
  Body,
  Cell,
  Column,
  Footer,
  Header,
  InputNumberCell,
  Row,
  Table,
  ToggleCell,
} from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconCheck from '@/icons/Check.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import { useUpdateWorkflowConfig, Workflow } from '@/models/workflow';
import { useDatabaseTranslation } from '@/services/i18n';

type RequestDueAtConfig = Exclude<Workflow['config']['requestDueAt'], undefined>[number];

const TitleAndDescription = () => (
  <Stack>
    <Title as="h2">
      <Trans id="settings.workflows.general.request-due-at.title">Request SLAs</Trans>
    </Title>
    <Paragraph size="200" color="secondary">
      <Trans id="settings.workflows.general.request-due-at.description">
        Number of working days after creation that the request is due. This is used in comparison to
        the estimated due date to determine if the request is overdue.
      </Trans>
    </Paragraph>
  </Stack>
);

export const RequestDueAt = ({ workflow }: { workflow: Workflow }) => {
  const [isEditing, setIsEditing] = useState(false);

  return isEditing ? (
    <RequestDueAtEdit workflow={workflow} onSaveOrCancel={() => setIsEditing(false)} />
  ) : (
    <RequestDueAtView workflow={workflow} onEdit={() => setIsEditing(true)} />
  );
};

const RequestDueAtView = ({ workflow, onEdit }: { workflow: Workflow; onEdit: () => void }) => {
  const { _db } = useDatabaseTranslation();

  return (
    <Box gap="1rem">
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <TitleAndDescription />
        <Button iconOnly variant="secondary" size="medium" onPress={onEdit}>
          <IconEdit />
        </Button>
      </Stack>
      <Table columnWidths={['2fr', '1fr']} bordered variant="grid">
        <Header>
          <Row>
            <Column>
              <Trans id="settings.workflows.general.request-due-at.table.header.label">Label</Trans>
            </Column>
            <Column>
              <Trans id="settings.workflows.general.request-due-at.table.header.days">
                Working days
              </Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          {workflow.config.requestDueAt?.map((item) => (
            <Row key={item.type}>
              <Cell>
                <Stack row alignItems="center" gap="0.5rem">
                  {item.typeTranslation ? (
                    _db(item.typeTranslation)
                  ) : (
                    <i>
                      <Trans id="settings.workflows.general.request-due-at.table.cell.label.placeholder">
                        Unnamed SLA
                      </Trans>
                    </i>
                  )}
                  {!item.enabled && (
                    <Badge color="neutral">
                      <Trans id="settings.workflows.general.request-due-at.table.cell.label.disabled">
                        Disabled
                      </Trans>
                    </Badge>
                  )}
                </Stack>
              </Cell>
              <Cell>{item.days}</Cell>
            </Row>
          ))}
        </Body>
      </Table>
    </Box>
  );
};

const RequestDueAtEdit = ({
  workflow,
  onSaveOrCancel,
}: {
  workflow: Workflow;
  onSaveOrCancel: () => void;
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { mutateAsync: updateWorkflowConfig, isPending } = useUpdateWorkflowConfig(workflow.id);
  const [requestDueAt, setRequestDueAt] = useState<RequestDueAtConfig[]>(
    workflow.config.requestDueAt ?? []
  );

  const handleSave = async () => {
    try {
      await updateWorkflowConfig({ requestDueAt });
      onSaveOrCancel();
      showToast({
        text: (
          <Trans id="settings.workflows.general.request-due-at.save.success">
            Request SLAs saved successfully.
          </Trans>
        ),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.workflows.general.request-due-at.save.error">
            Could not save request SLAs. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <Box gap="1rem">
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <TitleAndDescription />
        <Stack row gap="0.5rem">
          <Button
            iconOnly
            variant="primary"
            size="medium"
            onPress={handleSave}
            isLoading={isPending}
            disabled={isPending}
          >
            <IconCheck />
          </Button>
          <Button
            iconOnly
            variant="secondary-danger"
            size="medium"
            onPress={onSaveOrCancel}
            disabled={isPending}
          >
            <IconCross />
          </Button>
        </Stack>
      </Stack>
      <Table columnWidths={['2fr', '1fr', 'auto']} bordered variant="grid">
        <Header>
          <Row>
            <Column>
              <Trans id="settings.workflows.general.request-due-at.table.header.label">Label</Trans>
            </Column>
            <Column>
              <Trans id="settings.workflows.general.request-due-at.table.header.days">
                Working days
              </Trans>
            </Column>
            <Column>
              <Trans id="settings.workflows.general.request-due-at.table.header.enabled">
                Enabled?
              </Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          {requestDueAt.map((item) => {
            const updateItem = (partialItem: Partial<RequestDueAtConfig>) => {
              setRequestDueAt(
                requestDueAt.map((it) => (it.type === item.type ? { ...it, ...partialItem } : it))
              );
            };

            return (
              <Row key={item.type}>
                <Cell isEditable>
                  <TranslatableInputText
                    aria-label={t({
                      id: 'settings.workflows.general.request-due-at.table.cell.label.aria-label',
                      message: 'Edit SLA label',
                    })}
                    value={item.typeTranslation ?? { en: '', fr: '' }}
                    onChange={(typeTranslation) => updateItem({ typeTranslation })}
                  />
                </Cell>
                <InputNumberCell
                  aria-label={t({
                    id: 'settings.workflows.general.request-due-at.table.cell.days.aria-label',
                    message: 'Edit SLA working days',
                  })}
                  value={item.days}
                  onChange={(days) => updateItem({ days })}
                >
                  {item.days}
                </InputNumberCell>
                <ToggleCell
                  ariaLabel={t({
                    id: 'settings.workflows.general.request-due-at.table.cell.enabled.aria-label',
                    message: 'Enable/disable SLA',
                  })}
                  isSelected={item.enabled}
                  onChange={(enabled) => updateItem({ enabled })}
                />
              </Row>
            );
          })}
        </Body>
        <Footer>
          <Row>
            <Cell isWholeRow>
              <Stack row alignItems="center" gap="0.5rem" justifyContent="space-between">
                <Paragraph size="200" color="secondary">
                  <Trans id="settings.workflows.general.request-due-at.table.footer.add-description">
                    After adding a new SLA,{' '}
                    <strong>make sure you also edit each step's associated SLA</strong>. Otherwise,
                    the estimated due date will not be calculated correctly.
                  </Trans>
                </Paragraph>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() =>
                    setRequestDueAt([
                      ...requestDueAt,
                      {
                        type: crypto.randomUUID(),
                        days: 10,
                        enabled: true,

                        typeTranslation: {
                          // eslint-disable-next-line lingui/no-unlocalized-strings
                          en: 'Unnamed SLA',
                          // eslint-disable-next-line lingui/no-unlocalized-strings
                          fr: 'SLA sans nom',
                          ja: '名前なしSLA',
                        },
                      },
                    ])
                  }
                >
                  <IconAdd />
                  <Trans id="settings.workflows.general.request-due-at.table.footer.add">
                    Add SLA
                  </Trans>
                </Button>
              </Stack>
            </Cell>
          </Row>
        </Footer>
      </Table>
    </Box>
  );
};
